import { captureException } from '@sentry/react';
import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useFDMServices } from '../../../providers/fdm-services/fdm-services.context';
import { QueryKeys } from '../../../utils/queryKeys';
import type { ModelAndRevision3D } from '../types';
import type { QueryMeta } from '../../../providers/query-client';

export type ModelPage = {
  items: ModelAndRevision3D[];
  pageInfo: { hasNextPage: boolean; endCursor?: string };
};

export const use3dModelList = (pageSize?: number) => {
  const { threeDService } = useFDMServices();

  const { data, isLoading, hasNextPage, fetchNextPage, error } =
    useInfiniteQuery<
      ModelPage,
      Error,
      InfiniteData<ModelPage, string | undefined>,
      QueryKey,
      string | undefined
    >({
      meta: {
        error: { captureError: true },
      },
      initialPageParam: undefined,
      queryKey: [QueryKeys.THREE_D, 'model and revision list'],
      queryFn: async ({ pageParam = undefined }) => {
        const { models, pageInfo } = await threeDService.get3DModels(
          pageParam,
          pageSize
        );

        return {
          items: models,
          pageInfo: {
            hasNextPage: pageInfo.hasNextPage,
            endCursor: pageInfo.endCursor,
          },
        };
      },
      getNextPageParam: (lastPage) =>
        lastPage?.pageInfo.hasNextPage
          ? lastPage.pageInfo.endCursor
          : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const models = data?.pages.flatMap((page) => page?.items) || [];

  return { data: models, isLoading, hasNextPage, fetchNextPage, error };
};
